import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.min.json";

const CALLING_CODES = [
  ...Object.keys(metadata.country_calling_codes),
  ...Object.keys(metadata.nonGeographic),
];
const CALLING_CODE_SET = new Set(CALLING_CODES);

export interface PhonePrefixParts {
  prefix: string;
  number: string;
}

export interface PhoneCountryOption {
  countryCode: string;
  countryName: string;
  dialCode: string;
}

const countryNames = new Intl.DisplayNames(["es"], { type: "region" });
const countryNameCollator = new Intl.Collator("es");

export const PHONE_COUNTRY_OPTIONS: readonly PhoneCountryOption[] = getCountries()
  .map((countryCode) => ({
    countryCode,
    countryName: countryNames.of(countryCode) ?? countryCode,
    dialCode: `+${getCountryCallingCode(countryCode)}`,
  }))
  .sort((left, right) =>
    countryNameCollator.compare(left.countryName, right.countryName) ||
    left.countryCode.localeCompare(right.countryCode),
  );

const COUNTRIES_BY_DIAL_CODE = Map.groupBy(PHONE_COUNTRY_OPTIONS, ({ dialCode }) => dialCode);

export function resolvePhoneCountry(value: string): PhoneCountryOption | undefined {
  const { prefix, number } = splitPhonePrefix(value);
  if (!prefix) return undefined;

  const countries = COUNTRIES_BY_DIAL_CODE.get(prefix);
  if (countries?.length === 1) return countries[0];
  if (!countries?.length) return undefined;

  const countryCode = parsePhoneNumberFromString(joinPhonePrefix(prefix, number))?.country;
  return countries.find((country) => country.countryCode === countryCode);
}

export function splitPhonePrefix(value: string): PhonePrefixParts {
  const input = value.trimStart();
  const match = /^(?:\+|00)(\d+)(.*)$/s.exec(input);
  if (!match) return { prefix: "", number: value };

  const [, digits, suffix] = match;
  for (let length = Math.min(3, digits.length); length >= 1; length -= 1) {
    const code = digits.slice(0, length);
    if (CALLING_CODE_SET.has(code)) {
      return {
        prefix: `+${code}`,
        number: `${digits.slice(length)}${suffix}`.trimStart(),
      };
    }
  }

  return { prefix: "", number: value };
}

export function joinPhonePrefix(prefix: string, number: string): string {
  if (!prefix || /^\s*(?:\+|00)/.test(number)) return number;
  const code = prefix.startsWith("00") ? prefix.slice(2) : prefix.slice(1);
  if (!/^(?:\+|00)\d{1,3}$/.test(prefix) || !CALLING_CODE_SET.has(code)) {
    return number;
  }

  return number ? `${prefix} ${number}` : "";
}
